import type {
  SignerApplicantPutRequest,
  SignerPutAddressEntry,
  SignerPutAddressesSection,
  SignerPutApplicantSection,
  SignerPutBankSection,
  SignerPutEmailsSection,
  SignerPutEmploymentRow,
  SignerPutEmploymentsSection,
  SignerPutEmploymentsSectionDeleteSlotsEnum,
  SignerPutPhoneRow,
  SignerPutPhonesSection,
} from "@credit-acceptance/step6-bff-client";

import { INCOME_TYPE_CODE_TO_ENUM } from "./constants";
import type { BankAccountInput, CustomerInfoInput, IncomeInput, PersonInput } from "./Signers.schema";

// ---------------------------------------------------------------------------
// Signers.putMapping
//
// Maps a form PersonInput (driver or 2nd signer) onto the BFF
// SignerApplicantPutRequest for the aggregate save endpoint (POST
// /api/v1/workbench/applications/{approvalId}). Pure functions — no React —
// so they stay unit-testable and reusable for both signer legs.
//
// Only fields that exist in the form AND are BFF-required today are mapped.
// Every mapped field is always sent, blank or not (the BFF treats an omitted
// field as "no change" and an explicit "" as "clear" — see
// mapBankAccountToBankSection's isBankNameOptional handling — so omitting a
// blank field would silently leave a cleared value unsaved).
// Left unmapped for this ticket (no form input exists yet, so there's no
// value to send and omitting avoids clearing BFF-only data the form never
// displays):
//   - bank.accountNumber
//   - applicant.applicantPk, applicant.driverLicenseState
//   - addresses.*.residenceOwnerType, monthlyRentMortgage, landlord; previous[]
//   - phones.rows[].isCallDnc
//   - emails.rows[].isTextOptIn
//   - employments.*.workLocation (no form input for the work location object)
//
// employments.deleteSlots reports the trailing PRIMARY/SECONDARY/TERTIARY
// slot(s) that dropped out of the income array since the form loaded, purely
// as a diff between the original income count (from the GET hydration) and
// the current form income count — see OR-23465. Which row the analyst
// visually removed doesn't matter: removing any non-last row shifts the rest
// up, so the payload only needs to know how many slots contracted, and always
// reports the trailing ones (SECONDARY before TERTIARY).
//
// deleteSlots is typed by the generated client as Set<Enum>, but the client
// serializes request bodies with plain JSON.stringify (see step6-bff-client's
// serializeDataIfNeeded), which turns a real Set into `{}` on the wire. We
// build it as an array and cast it to the declared Set type below so the
// backend actually receives a JSON array.
// ---------------------------------------------------------------------------

/**
 * Strips every non-digit character from a string.
 * @param {string} value - The raw string.
 * @returns {string} Digits only.
 */
function digitsOnly(value: string): string {
  return value.replaceAll(/\D/g, "");
}

/**
 * Converts a blank/whitespace-only string to undefined so optional BFF fields
 * are omitted rather than sent as empty strings.
 * @param {string} value - The raw string.
 * @returns {string | undefined} The trimmed value, or undefined when blank.
 */
function undefinedIfBlank(value: string): string | undefined {
  return value.trim() === "" ? undefined : value;
}

/**
 * Joins a street number and street name into one address line.
 * @param {string} streetNumber - The street number.
 * @param {string} streetName - The street name.
 * @returns {string} The joined line, or "" when both are blank.
 */
function joinAddressLine(streetNumber: string, streetName: string): string {
  return [streetNumber, streetName].filter(Boolean).join(" ");
}

/**
 * Maps the form bank account onto the BFF bank section. A blank bank name is
 * sent as an explicit `""` (the BFF clears the stored value on an empty
 * string) only when Bank Name is optional for this deal; otherwise it's
 * omitted like every other optional field, since the BFF treats an explicit
 * `""` as "clear" rather than "unset".
 * @param {BankAccountInput} bankAccount - The form's bank account.
 * @param {boolean} isBankNameOptional - True when Bank Name validation is optional for this deal.
 * @returns {SignerPutBankSection} The BFF bank section.
 */
export function mapBankAccountToBankSection(
  bankAccount: BankAccountInput,
  isBankNameOptional: boolean
): SignerPutBankSection {
  return {
    bankName: isBankNameOptional ? (bankAccount.bankName ?? "") : undefinedIfBlank(bankAccount.bankName ?? ""),
    isCheckingAccount: bankAccount.isCheckingAccount,
    isSavingsAccount: bankAccount.isSavingsAccount,
    isPrepaidDebit: bankAccount.isPrepaidDebit,
  };
}

/**
 * Maps the form customer info's personal details onto the BFF applicant section.
 * @param {CustomerInfoInput} customerInfo - The form's customer information.
 * @returns {SignerPutApplicantSection} The BFF applicant section.
 */
export function mapCustomerInfoToApplicantSection(customerInfo: CustomerInfoInput): SignerPutApplicantSection {
  return {
    fullName: {
      first: customerInfo.firstName,
      middle: customerInfo.middleInitial,
      last: customerInfo.lastName,
      suffix: customerInfo.suffix,
    },
    dob: customerInfo.dateOfBirth,
    ssn: digitsOnly(customerInfo.ssnOrItin),
  };
}

/**
 * Builds one BFF address entry from raw street/city/state/zip fields. Always
 * returns a populated entry (blank fields as "") rather than omitting fields
 * or the whole entry, so a cleared address actually clears on save instead of
 * leaving the BFF's stored value untouched.
 * @param {string} streetNumber - Street number.
 * @param {string} streetName - Street name.
 * @param {string} apt - Apartment/suite number.
 * @param {string} zip - ZIP code.
 * @param {string} city - City.
 * @param {string} state - State.
 * @returns {SignerPutAddressEntry} The address entry.
 */
function mapAddressEntry(
  streetNumber: string,
  streetName: string,
  apt: string,
  zip: string,
  city: string,
  state: string
): SignerPutAddressEntry {
  return { line1: joinAddressLine(streetNumber, streetName), line2: apt, city, state, zip };
}

/**
 * Maps the form customer info's physical/mailing addresses onto the BFF
 * addresses section. A mailing address is only sent when it differs from the
 * physical one (`mailingSameAsCurrent` is false); `undefined` tells the BFF to
 * leave the mailing address as-is.
 * @param {CustomerInfoInput} customerInfo - The form's customer information.
 * @returns {SignerPutAddressesSection} The BFF addresses section.
 */
export function mapCustomerInfoToAddressesSection(customerInfo: CustomerInfoInput): SignerPutAddressesSection {
  const current = mapAddressEntry(
    customerInfo.physicalStreetNumber,
    customerInfo.physicalStreetName,
    customerInfo.physicalApartmentNumber,
    customerInfo.physicalZip,
    customerInfo.physicalCity,
    customerInfo.physicalState
  );

  const mailing = customerInfo.mailingSameAsCurrent
    ? undefined
    : mapAddressEntry(
        customerInfo.mailingStreetNumber,
        customerInfo.mailingStreetName,
        customerInfo.mailingApartmentNumber,
        customerInfo.mailingZip,
        customerInfo.mailingCity,
        customerInfo.mailingState
      );

  return { current, mailing };
}

/**
 * Maps the form customer info's phone rows onto the BFF phones section. Rows
 * with no digits entered are dropped; a present row's blank extension is sent
 * as "" (rather than omitted) so a cleared extension actually clears on save.
 * @param {CustomerInfoInput} customerInfo - The form's customer information.
 * @returns {SignerPutPhonesSection} The BFF phones section.
 */
export function mapCustomerInfoToPhonesSection(customerInfo: CustomerInfoInput): SignerPutPhonesSection {
  const rows: SignerPutPhoneRow[] = customerInfo.phones
    .map((phone) => ({
      type: phone.type.toUpperCase(),
      number: digitsOnly(phone.number),
      extension: digitsOnly(phone.extension),
    }))
    .filter((phone) => phone.number.length > 0);

  return { rows };
}

/**
 * Maps the form customer info's email onto the BFF emails section.
 * @param {CustomerInfoInput} customerInfo - The form's customer information.
 * @returns {SignerPutEmailsSection} The BFF emails section.
 */
export function mapCustomerInfoToEmailsSection(customerInfo: CustomerInfoInput): SignerPutEmailsSection {
  const emailAddress = undefinedIfBlank(customerInfo.emailAddress);

  return {
    rows: emailAddress ? [{ emailAddress }] : [],
    isEmailAvailable: !customerInfo.emailNotAvailable,
  };
}

/**
 * Maps a single form income block onto a BFF employment row. `incomeType`
 * carries the BFF's income-type enum value; `incomeTypeCode` (despite its
 * name) carries the pay-frequency enum value.
 * @param {IncomeInput} income - A form income block.
 * @returns {SignerPutEmploymentRow} The BFF employment row.
 */
function mapIncomeToEmploymentRow(income: IncomeInput): SignerPutEmploymentRow {
  return {
    incomeType: INCOME_TYPE_CODE_TO_ENUM[income.employmentType],
    incomeTypeCode: income.incomeFrequencyType,
    payType: income.payType,
    grossPay: income.monthlyGrossPayAmount,
    employerPhone: digitsOnly(income.employerPhone),
    employerName: income.employerName,
    hireDate: income.hireDate,
    occupation: income.occupation ?? "",
    streetNumber: income.streetNumber ?? "",
    streetName: income.streetName ?? "",
    apartmentNumber: income.apartmentNumber ?? "",
    city: income.city ?? "",
    state: income.state ?? "",
    zip: income.zip ?? "",
    phoneExtension: income.phoneExtension ?? "",
    incomePerPayPeriodAmount: income.incomePerPayPeriodAmount,
    notes: income.notes ?? "",
    payStubDate: income.payStubDate ?? "",
    payPeriodEndDate: income.payPeriodEndDate ?? "",
    yearToDateIncomeAmount: income.yearToDateIncomeAmount,
  };
}

/** Index-to-slot lookup for the first three income blocks; index 0 is PRIMARY. */
export const EMPLOYMENT_SLOTS_BY_INDEX = ["PRIMARY", "SECONDARY", "TERTIARY"] as const;

/**
 * Maps the form's income blocks onto the BFF employments section.
 * `totalGrossMonthlyIncome` is the sum of every block's gross monthly pay.
 * `deleteSlots` is derived from the drop in income count since the form
 * loaded — see the module comment and OR-23465's count table.
 * @param {IncomeInput[]} incomes - The form's income blocks (1-3).
 * @param {number} [originalIncomeCount] - The income block count as of the last hydration or successful save (re-baselined by the caller, not fixed at mount).
 * @returns {SignerPutEmploymentsSection} The BFF employments section.
 */
export function mapIncomesToEmploymentsSection(
  incomes: IncomeInput[],
  originalIncomeCount?: number
): SignerPutEmploymentsSection {
  const [primary, secondary, tertiary] = incomes;
  const removedSlots =
    originalIncomeCount !== undefined && originalIncomeCount > incomes.length
      ? EMPLOYMENT_SLOTS_BY_INDEX.slice(incomes.length, originalIncomeCount)
      : [];

  return {
    primary: primary ? mapIncomeToEmploymentRow(primary) : undefined,
    secondary: secondary ? mapIncomeToEmploymentRow(secondary) : undefined,
    tertiary: tertiary ? mapIncomeToEmploymentRow(tertiary) : undefined,
    totalGrossMonthlyIncome: incomes.reduce((total, income) => total + income.monthlyGrossPayAmount, 0),
    deleteSlots:
      removedSlots.length > 0
        ? (removedSlots as unknown as Set<SignerPutEmploymentsSectionDeleteSlotsEnum>)
        : undefined,
  };
}

/**
 * Builds the full BFF `SignerApplicantPutRequest` from a form person (driver or
 * 2nd signer), for the `signerApplicant` / `secondSignerApplicant` legs of the
 * aggregate save payload.
 * @param {PersonInput} person - The form's driver or 2nd-signer state.
 * @param {boolean} isBankNameOptional - True when Bank Name validation is optional for this deal. See {@link mapBankAccountToBankSection}.
 * @param {number} [originalIncomeCount] - The income block count as of the last hydration or successful save. See {@link mapIncomesToEmploymentsSection}.
 * @returns {SignerApplicantPutRequest} The BFF signer-applicant PUT request.
 */
export function buildSignerApplicantPutRequest(
  person: PersonInput,
  isBankNameOptional: boolean,
  originalIncomeCount?: number
): SignerApplicantPutRequest {
  return {
    applicant: mapCustomerInfoToApplicantSection(person.customerInfo),
    addresses: mapCustomerInfoToAddressesSection(person.customerInfo),
    phones: mapCustomerInfoToPhonesSection(person.customerInfo),
    emails: mapCustomerInfoToEmailsSection(person.customerInfo),
    employments: mapIncomesToEmploymentsSection(person.incomes, originalIncomeCount),
    bank: mapBankAccountToBankSection(person.bankAccount, isBankNameOptional),
  };
}
